import { ArrowLeft, BookOpen, Compass, SearchX } from 'lucide-react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { cn } from '../lib/cn.js';
import { Button } from './Button.jsx';
import { Text } from './Text.jsx';

export function isNotFoundMessage(message) {
  return /(^|\s)(404)(\s|$)|not found|not available/i.test(String(message ?? ''));
}

export function NotFoundPage({
  eyebrow = '404',
  title = "We couldn't find that page.",
  description = 'The link may be outdated, or this item may not be available for your account.',
  primaryTo = '/courses',
  primaryLabel = 'Back to My Courses',
  secondaryTo,
  secondaryLabel = 'More Courses',
  className,
}) {
  const navigate = useNavigate();

  return (
    <section
      className={cn(
        'min-h-[60vh] w-full max-w-4xl py-8 sm:py-12',
        'flex items-center',
        className,
      )}
      aria-labelledby="not-found-title"
    >
      <div className="w-full">
        <div className="mb-6 inline-flex items-center gap-2 rounded-pill border border-line bg-paper px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted">
          <SearchX className="h-4 w-4 text-teal-dark" aria-hidden />
          {eyebrow}
        </div>

        <div className="grid gap-8 lg:grid-cols-[1fr_280px] lg:items-center">
          <div>
            <Text id="not-found-title" variant="h2" className="max-w-xl">
              {title}
            </Text>
            <Text variant="lede" className="mt-3 max-w-2xl">
              {description}
            </Text>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <RouterLink to={primaryTo}>
                <Button>
                  <ArrowLeft className="h-4 w-4" aria-hidden />
                  {primaryLabel}
                </Button>
              </RouterLink>
              {secondaryTo ? (
                <RouterLink to={secondaryTo}>
                  <Button variant="secondary">
                    <BookOpen className="h-4 w-4" aria-hidden />
                    {secondaryLabel}
                  </Button>
                </RouterLink>
              ) : (
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  Go back
                </Button>
              )}
            </div>
          </div>

          <div
            className="relative hidden h-56 rounded-card border border-line bg-teal-soft/70 p-5 sm:block"
            aria-hidden
          >
            <div className="absolute left-5 top-5 font-display text-7xl font-semibold leading-none text-teal-dark">
              4
            </div>
            <div className="absolute left-1/2 top-1/2 grid h-24 w-24 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-line bg-paper shadow-sm">
              <Compass className="h-10 w-10 text-teal-dark" />
            </div>
            <div className="absolute bottom-5 right-5 font-display text-7xl font-semibold leading-none text-teal-dark">
              4
            </div>
            <div className="absolute bottom-7 left-5 h-2 w-24 rounded-pill bg-paper" />
            <div className="absolute right-5 top-8 h-2 w-20 rounded-pill bg-paper" />
          </div>
        </div>
      </div>
    </section>
  );
}
